import React, { useEffect, useMemo, useState } from "react";
import { TBody, displayUnexpectedErrorAndLog } from "@eatbetter/ui-shared";
import {
  AppUserRecipe,
  ExternalRecipe,
  ExternalUrlRecipe,
  isAddedPhotoEligible,
  UserRecipe,
} from "@eatbetter/recipes-shared";
import { Button, Checkbox, InputNumber, Modal, Select, Spin } from "antd";
import { useDispatch } from "../lib/AdminRedux";
import { getVideoMetaForRecipe, publishAdminMessage } from "../lib/recipes/AdminRecipesThunks";
import type { StoredVideoMeta } from "@eatbetter/recipes-server/dist/internal/RecipeRawDataStore";
import type {
  ExtractCarouselRecipeMessage,
  ParsePhotoIngredientsMessage,
  ParsePhotoInstructionsMessage,
  ParsePhotoTimeYieldDescriptionMessage,
  ProcessExternalUrlRecipeAiMessage,
  ProcessExternalUrlRecipeMessage,
  ProcessTags,
  RecipeIngestionMessage,
  ReprocessSocialVideoRecipe,
  ResolveBookForPhotoMessage,
  UserAddedPhotoRecipeMessage,
  UserAddedPhotosToRecipeMessage,
  UserAddedUrlMessage,
} from "@eatbetter/recipes-server";

// ─── Shared types ─────────────────────────────────────────────────────────────

type FormType =
  | "userReprocess"
  | "addedPhotosReprocess"
  | "userPhotoReprocess"
  | "tagsReprocess"
  | "externalReprocess"
  | "externalAiReprocess"
  | "externalVideoReprocess";

type MaxAgeUnit = "seconds" | "minutes" | "hours" | "days" | "years";
type AiFieldOption = "set" | "sectionCheck" | "none";
type AiIngredientsFieldOption = AiFieldOption | "likelyImprovement";
type OnMessagesChanged = (messages: RecipeIngestionMessage[]) => void;

const unitMultipliers: Record<MaxAgeUnit, number> = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
  years: 365 * 24 * 3600,
};

// ─── Top-level modal ──────────────────────────────────────────────────────────

export const ReprocessRecipeModal = React.memo(
  (props: { recipe: UserRecipe | AppUserRecipe | ExternalRecipe; open: boolean; onClose: () => void }) => {
    const dispatch = useDispatch();
    const { recipe } = props;

    const availableForms: { value: FormType; label: string }[] = useMemo(() => {
      if (recipe.type === "userRecipe") {
        return recipe.source.type === "userPhoto"
          ? [
              { value: "userPhotoReprocess" as const, label: "Reprocess Photos" },
              { value: "tagsReprocess" as const, label: "Process Tags" },
            ]
          : [
              { value: "userReprocess" as const, label: "Reprocess" },
              { value: "addedPhotosReprocess" as const, label: "Reprocess Added Photos" },
              { value: "tagsReprocess" as const, label: "Process Tags" },
            ];
      }
      if (recipe.type === "externalUrlRecipe") {
        return [
          { value: "externalReprocess" as const, label: "Reprocess" },
          { value: "externalAiReprocess" as const, label: "Reprocess AI" },
          { value: "externalVideoReprocess" as const, label: "Process Video" },
          { value: "tagsReprocess" as const, label: "Process Tags" },
        ];
      }
      // externalBookRecipe
      return [
        { value: "externalAiReprocess" as const, label: "Reprocess AI" },
        { value: "tagsReprocess" as const, label: "Process Tags" },
      ];
    }, [recipe]);

    const [selectedForm, setSelectedForm] = useState<FormType>(availableForms[0]!.value);
    const [messages, setMessages] = useState<RecipeIngestionMessage[]>([]);
    const [showMessageJson, setShowMessageJson] = useState(false);
    const [waiting, setWaiting] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
      if (props.open) {
        setSuccess(false);
      }
    }, [props.open]);

    useEffect(() => {
      if (success) {
        const timer = setTimeout(() => props.onClose(), 2000);
        return () => clearTimeout(timer);
      }

      return () => {};
    }, [success]);

    const handleFormChange = (form: FormType) => {
      setMessages([]);
      setSelectedForm(form);
    };

    const onOk = async () => {
      try {
        setWaiting(true);
        try {
          for (const message of messages) {
            await dispatch(publishAdminMessage({ message }));
          }
        } finally {
          setWaiting(false);
        }
        setSuccess(true);
      } catch (err) {
        displayUnexpectedErrorAndLog("Error in ReprocessRecipeModal publishAdminMessage", err);
      }
    };

    const renderForm = () => {
      if (selectedForm === "userReprocess" && recipe.type === "userRecipe") {
        return <UserUrlReprocessForm key={selectedForm} recipe={recipe} onMessagesChanged={setMessages} />;
      }
      if (selectedForm === "addedPhotosReprocess" && recipe.type === "userRecipe") {
        return <AddedPhotosReprocessForm key={selectedForm} recipe={recipe} onMessagesChanged={setMessages} />;
      }
      if (selectedForm === "userPhotoReprocess" && recipe.type === "userRecipe") {
        return <UserPhotoReprocessForm key={selectedForm} recipe={recipe} onMessagesChanged={setMessages} />;
      }
      if (selectedForm === "externalReprocess" && recipe.type === "externalUrlRecipe") {
        return <ExternalUrlReprocessForm key={selectedForm} recipe={recipe} onMessagesChanged={setMessages} />;
      }
      if (
        selectedForm === "externalAiReprocess" &&
        (recipe.type === "externalUrlRecipe" || recipe.type === "externalBookRecipe")
      ) {
        // A carousel member's AI content comes from the carousel extraction chain, not the normal AI
        // parser — so reprocess it via extractCarouselRecipe instead of processExternalUrlRecipeAi.
        if (recipe.type === "externalUrlRecipe" && recipe.multiSource) {
          return <CarouselExtractReprocessForm key={selectedForm} recipe={recipe} onMessagesChanged={setMessages} />;
        }
        return <ExternalUrlAiReprocessForm key={selectedForm} recipe={recipe} onMessagesChanged={setMessages} />;
      }
      if (selectedForm === "externalVideoReprocess" && recipe.type === "externalUrlRecipe") {
        return <ExternalVideoReprocessForm key={selectedForm} recipe={recipe} onMessagesChanged={setMessages} />;
      }
      if (selectedForm === "tagsReprocess") {
        return <TagsReprocessForm key={selectedForm} recipe={recipe} onMessagesChanged={setMessages} />;
      }
      return (
        <TBody color="red">
          Unexpected form/recipe type combination: {selectedForm} / {recipe.type}
        </TBody>
      );
    };

    const messageJsonPreview =
      messages.length === 1 ? JSON.stringify(messages[0], null, 2) : JSON.stringify(messages, null, 2);

    return (
      <Modal
        title="Reprocess"
        open={props.open}
        onCancel={props.onClose}
        width={800}
        footer={
          success
            ? [
                <Button key="close" type="primary" onClick={props.onClose}>
                  Close
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={props.onClose}>
                  Cancel
                </Button>,
                <Button key="see" onClick={() => setShowMessageJson(v => !v)}>
                  {showMessageJson
                    ? `Hide Message${messages.length > 1 ? "s" : ""}`
                    : `See Message${messages.length > 1 ? "s" : ""}`}
                </Button>,
                <Button key="publish" type="primary" loading={waiting} disabled={messages.length === 0} onClick={onOk}>
                  Publish
                </Button>,
              ]
        }
      >
        {success ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <TBody>Message published successfully.</TBody>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16, padding: "8px 12px", background: "#f5f5f5", borderRadius: 4 }}>
              <TBody fontWeight="heavy">Recipe ID: </TBody>
              <TBody>{recipe.id}</TBody>
            </div>
            {availableForms.length > 1 && (
              <div style={{ marginBottom: 12 }}>
                <Select<FormType>
                  value={selectedForm}
                  onChange={handleFormChange}
                  style={{ width: "100%", fontSize: 16 }}
                >
                  {availableForms.map(f => (
                    <Select.Option key={f.value} value={f.value}>
                      {f.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}
            {renderForm()}
            {showMessageJson && (
              <pre
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: "#f5f5f5",
                  borderRadius: 4,
                  maxHeight: 300,
                  overflow: "auto",
                  fontSize: 12,
                }}
              >
                {messageJsonPreview}
              </pre>
            )}
          </>
        )}
      </Modal>
    );
  }
);

// ─── Form components ──────────────────────────────────────────────────────────

const UserUrlReprocessForm = React.memo(
  ({ recipe, onMessagesChanged }: { recipe: UserRecipe | AppUserRecipe; onMessagesChanged: OnMessagesChanged }) => {
    useEffect(() => {
      const payload: UserAddedUrlMessage = {
        type: "userAddedUrl",
        userId: recipe.userId,
        recipeId: recipe.id,
        reprocess: true,
      };
      onMessagesChanged([{ messageType: "recipes/ingestionMessage", message: payload }]);
    }, []);

    return (
      <TBody>
        Re-fetches and reprocesses the recipe from its source URL. Equivalent to the user re-adding the URL from
        scratch.
      </TBody>
    );
  }
);

const AddedPhotosReprocessForm = React.memo(
  ({ recipe, onMessagesChanged }: { recipe: UserRecipe | AppUserRecipe; onMessagesChanged: OnMessagesChanged }) => {
    const photoCount = recipe.media.filter(isAddedPhotoEligible).length;

    useEffect(() => {
      if (photoCount === 0) {
        onMessagesChanged([]);
        return;
      }
      const payload: UserAddedPhotosToRecipeMessage = {
        type: "userAddedPhotosToRecipe",
        recipeId: recipe.id,
        reprocessAll: true,
      };
      onMessagesChanged([{ messageType: "recipes/ingestionMessage", message: payload }]);
    }, []);

    if (photoCount === 0) {
      return <TBody>This recipe has no photos added by the user.</TBody>;
    }

    return (
      <>
        <TBody>
          Re-checks the {photoCount} photo{photoCount === 1 ? "" : "s"} the user added for recipe content and merges any
          content into the current recipe.
        </TBody>
        <div style={{ marginTop: 12 }}>
          <TBody color="red">
            Warning: if a photo changes the recipe, this creates a new user recipe version and counts as a user edit.
          </TBody>
        </div>
      </>
    );
  }
);

const UserPhotoReprocessForm = React.memo(
  ({ recipe, onMessagesChanged }: { recipe: UserRecipe | AppUserRecipe; onMessagesChanged: OnMessagesChanged }) => {
    const [parseIngredients, setParseIngredients] = useState(true);
    const [parseInstructions, setParseInstructions] = useState(true);
    const [parseTimeYieldDescription, setParseTimeYieldDescription] = useState(true);
    const [resolveBook, setResolveBook] = useState(true);

    const allChecked = parseIngredients && parseInstructions && parseTimeYieldDescription && resolveBook;
    const noneChecked = !parseIngredients && !parseInstructions && !parseTimeYieldDescription && !resolveBook;

    useEffect(() => {
      if (noneChecked) {
        onMessagesChanged([]);
        return;
      }
      if (allChecked) {
        const payload: UserAddedPhotoRecipeMessage = { type: "photos/userAddedPhotoRecipe", recipeId: recipe.id };
        onMessagesChanged([{ messageType: "recipes/ingestionMessage", message: payload }]);
        return;
      }
      const messages: RecipeIngestionMessage[] = [];
      if (parseIngredients) {
        const payload: ParsePhotoIngredientsMessage = { type: "photos/parseIngredients", recipeId: recipe.id };
        messages.push({ messageType: "recipes/ingestionMessage", message: payload });
      }
      if (parseInstructions) {
        const payload: ParsePhotoInstructionsMessage = { type: "photos/parseInstructions", recipeId: recipe.id };
        messages.push({ messageType: "recipes/ingestionMessage", message: payload });
      }
      if (parseTimeYieldDescription) {
        const payload: ParsePhotoTimeYieldDescriptionMessage = {
          type: "photos/parseTimeYieldDescription",
          recipeId: recipe.id,
        };
        messages.push({ messageType: "recipes/ingestionMessage", message: payload });
      }
      if (resolveBook) {
        const payload: ResolveBookForPhotoMessage = { type: "photos/resolveBookForPhotoRecipe", recipeId: recipe.id };
        messages.push({ messageType: "recipes/ingestionMessage", message: payload });
      }
      onMessagesChanged(messages);
    }, [parseIngredients, parseInstructions, parseTimeYieldDescription, resolveBook]);

    return (
      <>
        <TBody>
          {allChecked
            ? "Publishes the initial photo recipe message, triggering all queries from scratch."
            : "Publishes individual query messages for the selected options."}
        </TBody>
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <Checkbox checked={parseIngredients} onChange={e => setParseIngredients(e.target.checked)}>
            <TBody>Ingredients</TBody>
          </Checkbox>
          <Checkbox checked={parseInstructions} onChange={e => setParseInstructions(e.target.checked)}>
            <TBody>Instructions</TBody>
          </Checkbox>
          <Checkbox checked={parseTimeYieldDescription} onChange={e => setParseTimeYieldDescription(e.target.checked)}>
            <TBody>Time / Yield / Description</TBody>
          </Checkbox>
          <Checkbox checked={resolveBook} onChange={e => setResolveBook(e.target.checked)}>
            <TBody>Resolve Book</TBody>
          </Checkbox>
        </div>
      </>
    );
  }
);

const ExternalUrlReprocessForm = React.memo(
  ({ recipe, onMessagesChanged }: { recipe: ExternalUrlRecipe; onMessagesChanged: OnMessagesChanged }) => {
    const [maxAgeValue, setMaxAgeValue] = useState<number | undefined>(10);
    const [maxAgeUnit, setMaxAgeUnit] = useState<MaxAgeUnit>("years");
    const [applyMaxAgeToUserHtml, setApplyMaxAgeToUserHtml] = useState(false);
    const [applyMaxAgeToAdminHtml, setApplyMaxAgeToAdminHtml] = useState(false);

    const forceRefetch = maxAgeValue === 0;

    const onForceRefetchChange = (checked: boolean) => {
      if (checked) {
        setMaxAgeValue(0);
        setMaxAgeUnit("seconds");
      } else {
        setMaxAgeValue(10);
        setMaxAgeUnit("years");
      }
    };

    useEffect(() => {
      const maxAgeInSeconds = Math.round((maxAgeValue ?? 0) * unitMultipliers[maxAgeUnit]);
      const payload: ProcessExternalUrlRecipeMessage = {
        type: "processExternalUrlRecipe",
        externalRecipeId: recipe.id,
        reprocess: true,
        openTask: false,
        maxAgeInSecondsForCachedHtml: maxAgeInSeconds,
        applyMaxAgeToUserHtml,
        applyMaxAgeToAdminHtml,
      };
      onMessagesChanged([{ messageType: "recipes/ingestionMessage", message: payload }]);
    }, [maxAgeValue, maxAgeUnit, applyMaxAgeToUserHtml, applyMaxAgeToAdminHtml]);

    return (
      <>
        <TBody>
          Re-fetches (optionally) and reprocesses the recipe from its source URL, even if processing has already
          completed. Use this to pick up content changes from the publisher&apos;s site or to reprocess existing data
          with an updated parser.
        </TBody>
        <div style={{ marginTop: 8 }}>
          <TBody color="#d46b08">
            WARNING: If the recipe has been AI processed, this will cause the recipe to temporarily go back to an
            initial (worse) state as processing happens. If you only want to re-run AI processing, use &quot;Reprocess
            AI&quot;
          </TBody>
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={formStyles.labelContainer}>
            <TBody fontWeight="heavy" {...formStyles.label}>
              Max age for cached HTML
            </TBody>
          </div>
          <div style={formStyles.labelContainer}>
            <Checkbox checked={forceRefetch} onChange={e => onForceRefetchChange(e.target.checked)}>
              <TBody {...formStyles.label}>Force re-fetch</TBody>
            </Checkbox>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <InputNumber<number>
              value={maxAgeValue}
              onChange={v => setMaxAgeValue(v ?? 0)}
              min={0}
              style={{ width: 120 }}
            />
            <Select<MaxAgeUnit> value={maxAgeUnit} onChange={setMaxAgeUnit} style={{ width: 120 }}>
              <Select.Option value="seconds">seconds</Select.Option>
              <Select.Option value="minutes">minutes</Select.Option>
              <Select.Option value="hours">hours</Select.Option>
              <Select.Option value="days">days</Select.Option>
              <Select.Option value="years">years</Select.Option>
            </Select>
          </div>
          <div style={{ marginTop: 8 }}>
            <TBody color="#999">By default, the max age does not apply to user and admin HTML.</TBody>
          </div>
          <div>
            <Checkbox checked={applyMaxAgeToUserHtml} onChange={e => setApplyMaxAgeToUserHtml(e.target.checked)}>
              <TBody {...formStyles.label}>Apply max age to user html</TBody>
            </Checkbox>
          </div>
          <div>
            <Checkbox checked={applyMaxAgeToAdminHtml} onChange={e => setApplyMaxAgeToAdminHtml(e.target.checked)}>
              <TBody {...formStyles.label}>Apply max age to admin html</TBody>
            </Checkbox>
          </div>
        </div>
      </>
    );
  }
);

const CarouselExtractReprocessForm = React.memo(
  ({ recipe, onMessagesChanged }: { recipe: ExternalUrlRecipe; onMessagesChanged: OnMessagesChanged }) => {
    useEffect(() => {
      if (!recipe.multiSource) {
        onMessagesChanged([]);
        return;
      }
      const payload: ExtractCarouselRecipeMessage = {
        type: "extractCarouselRecipe",
        groupId: recipe.multiSource.groupId,
        recipeId: recipe.id,
        reprocess: true,
      };
      onMessagesChanged([{ messageType: "recipes/ingestionMessage", message: payload }]);
      // recipe identity is stable for the life of this form (keyed by selectedForm)
    }, []);

    return (
      <TBody>
        This recipe is part of a media carousel group. Reprocessing re-runs the carousel AI extraction for this member —
        title, ingredients, instructions, and key photo — from the group&apos;s stored media. If the re-extraction comes
        back empty, the existing recipe is preserved rather than blown away.
      </TBody>
    );
  }
);

const ExternalUrlAiReprocessForm = React.memo(
  ({ recipe, onMessagesChanged }: { recipe: ExternalRecipe; onMessagesChanged: OnMessagesChanged }) => {
    const manuallyUpdatedFields = recipe.type === "externalUrlRecipe" ? recipe.manuallyUpdatedFields : [];
    const titleAdminEdited = manuallyUpdatedFields.includes("title");
    const ingredientsAdminEdited = manuallyUpdatedFields.includes("ingredients");
    const instructionsAdminEdited = manuallyUpdatedFields.includes("instructions");

    const [aiTitleField, setAiTitleField] = useState(!titleAdminEdited);
    const [aiIngredientsField, setAiIngredientsField] = useState<AiIngredientsFieldOption>(
      ingredientsAdminEdited ? "none" : "set"
    );
    const [aiInstructionsField, setAiInstructionsField] = useState<AiFieldOption>(
      instructionsAdminEdited ? "none" : "set"
    );
    // A single-recipe carousel derives its content from the post's stored images (socialPostInfo.media),
    // so a reprocess MUST include them — lock the option on. Gate on isCarouselRecipe, NOT recipe.photo:
    // the images come from the carousel media (guaranteed present once analyzed), not the key photo, so a
    // carousel with no key photo must still reprocess with images rather than silently degrading to a
    // caption-only parse. (Multi-recipe carousel members go through CarouselExtractReprocessForm, which
    // always uses the group's media.)
    const isCarouselRecipe =
      recipe.type === "externalUrlRecipe" && recipe.processingMeta?.type === "carouselProcessingMeta";
    const [includeImages, setIncludeImages] = useState(isCarouselRecipe);
    const [aiHallucinationThreshold, setAiHallucinationThreshold] = useState<number | undefined>(undefined);

    useEffect(() => {
      if (!aiTitleField && aiIngredientsField === "none" && aiInstructionsField === "none") {
        onMessagesChanged([]);
        return;
      }
      const payload: ProcessExternalUrlRecipeAiMessage = {
        type: "processExternalUrlRecipeAi",
        externalRecipeId: recipe.id,
        reprocess: true,
        fields: {
          ...(aiTitleField ? { title: "set" as const } : {}),
          ...(aiIngredientsField !== "none" ? { ingredients: aiIngredientsField } : {}),
          ...(aiInstructionsField !== "none" ? { instructions: aiInstructionsField } : {}),
        },
        ...(includeImages ? { query: "images" as const } : {}),
        ...(aiHallucinationThreshold !== undefined ? { hallucinationThreshold: aiHallucinationThreshold } : {}),
      };
      onMessagesChanged([{ messageType: "recipes/ingestionMessage", message: payload }]);
    }, [aiTitleField, aiIngredientsField, aiInstructionsField, includeImages, aiHallucinationThreshold]);

    return (
      <>
        <TBody>
          Re-runs AI-based post-processing on the recipe. Does not re-fetch HTML or re-run the base parser — safe to use
          without reverting the recipe to an unprocessed state.
        </TBody>
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ opacity: titleAdminEdited ? 0.45 : 1 }}>
            <Checkbox
              checked={aiTitleField}
              disabled={titleAdminEdited}
              onChange={e => setAiTitleField(e.target.checked)}
            >
              Title
            </Checkbox>
            {titleAdminEdited && <TBody color="#999"> (Admin edited)</TBody>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, opacity: ingredientsAdminEdited ? 0.45 : 1 }}>
            <TBody>Ingredients</TBody>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Select<AiIngredientsFieldOption>
                value={aiIngredientsField}
                disabled={ingredientsAdminEdited}
                onChange={setAiIngredientsField}
                style={{ width: 170 }}
              >
                <Select.Option value="none">none</Select.Option>
                <Select.Option value="set">set</Select.Option>
                <Select.Option value="sectionCheck">sectionCheck</Select.Option>
                <Select.Option value="likelyImprovement">likelyImprovement</Select.Option>
              </Select>
              {ingredientsAdminEdited && <TBody color="#999">(Admin edited)</TBody>}
            </div>
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: 4, opacity: instructionsAdminEdited ? 0.45 : 1 }}
          >
            <TBody>Instructions</TBody>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Select<AiFieldOption>
                value={aiInstructionsField}
                disabled={instructionsAdminEdited}
                onChange={setAiInstructionsField}
                style={{ width: 140 }}
              >
                <Select.Option value="none">none</Select.Option>
                <Select.Option value="set">set</Select.Option>
                <Select.Option value="sectionCheck">sectionCheck</Select.Option>
              </Select>
              {instructionsAdminEdited && <TBody color="#999">(Admin edited)</TBody>}
            </div>
          </div>
          <div style={{ opacity: isCarouselRecipe || recipe.photo ? 1 : 0.45 }}>
            <Checkbox
              checked={includeImages}
              disabled={!recipe.photo || isCarouselRecipe}
              onChange={e => setIncludeImages(e.target.checked)}
            >
              Include Images
            </Checkbox>
            {isCarouselRecipe ? (
              <TBody color="#999"> This is a carousel post so images are required</TBody>
            ) : (
              !recipe.photo && <TBody color="#999"> There is no image</TBody>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <TBody>Hallucination threshold</TBody>
            <TBody color="#999">0–1; results used if score ≥ threshold; lower = more permissive</TBody>
            <InputNumber<number>
              value={aiHallucinationThreshold}
              onChange={v => setAiHallucinationThreshold(v ?? undefined)}
              min={0}
              max={1}
              step={0.05}
              style={{ width: 120 }}
              placeholder="default"
            />
          </div>
        </div>
      </>
    );
  }
);

const ExternalVideoReprocessForm = React.memo(
  ({ recipe, onMessagesChanged }: { recipe: ExternalUrlRecipe; onMessagesChanged: OnMessagesChanged }) => {
    const dispatch = useDispatch();
    const [forceVideoProcessing, setForceVideoProcessing] = useState(true);
    const [reprocessVideoDownload, setReprocessVideoDownload] = useState(false);
    const [reprocessDerived, setReprocessDerived] = useState(false);
    const [maxVideoLengthInMinutes, setMaxVideoLengthInMinutes] = useState<number | undefined>(60);
    const [videoMeta, setVideoMeta] = useState<StoredVideoMeta | undefined>(undefined);
    const [videoMetaLoading, setVideoMetaLoading] = useState(false);

    useEffect(() => {
      setVideoMetaLoading(true);
      dispatch(getVideoMetaForRecipe({ recipeId: recipe.id }))
        .then(meta => setVideoMeta(meta))
        .catch(err => displayUnexpectedErrorAndLog("Error fetching video meta", err))
        .finally(() => setVideoMetaLoading(false));
    }, []);

    useEffect(() => {
      const payload: ReprocessSocialVideoRecipe = {
        type: "reprocessSocialVideoRecipe",
        recipeId: recipe.id,
        ...(forceVideoProcessing ? { force: true } : {}),
        ...(reprocessVideoDownload ? { reprocess: true } : {}),
        ...(reprocessDerived ? { reprocessDerived: true } : {}),
        ...(maxVideoLengthInMinutes !== undefined ? { maxVideoLengthInMinutes } : {}),
      };
      onMessagesChanged([{ messageType: "recipes/ingestionMessage", message: payload }]);
    }, [forceVideoProcessing, reprocessVideoDownload, reprocessDerived, maxVideoLengthInMinutes]);

    return (
      <>
        <TBody>
          Triggers social media video processing for this recipe. Without Force, only applies to URLs detected as social
          media videos; the server will no-op for other URLs.
        </TBody>
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 24 }}>
          <div>
            <Checkbox checked={forceVideoProcessing} onChange={e => setForceVideoProcessing(e.target.checked)}>
              <TBody {...formStyles.label}>Force</TBody>
            </Checkbox>
            <TBody color="#999">
              {" "}
              When checked, bypasses the social-media-video URL check and the &quot;recipe already looks complete&quot;
              check. Use this when the URL is a video but isn&apos;t being detected as one, or when the video provides
              additional context beyond a seemingly complete recipe in the caption.
            </TBody>
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {videoMetaLoading ? (
                <Spin size="small" />
              ) : (
                <>
                  <Checkbox
                    checked={reprocessDerived}
                    onChange={e => {
                      setReprocessDerived(e.target.checked);
                      if (e.target.checked) setReprocessVideoDownload(false);
                    }}
                    disabled={!videoMeta}
                  >
                    <TBody {...formStyles.label}>Reprocess derived</TBody>
                  </Checkbox>
                  {!videoMeta && <TBody color="#999">(no video)</TBody>}
                </>
              )}
            </div>
            {!videoMetaLoading && videoMeta && (
              <TBody color="#999">
                Re-runs transcript and analysis from the stored video. Use only if there is a problem with one of these
                outputs (see docs to confirm)
              </TBody>
            )}
          </div>
          <div>
            <Checkbox
              checked={reprocessVideoDownload}
              onChange={e => {
                setReprocessVideoDownload(e.target.checked);
                if (e.target.checked) setReprocessDerived(false);
              }}
            >
              <TBody {...formStyles.label}>Re-download video</TBody>
            </Checkbox>
            <TBody color="#999">
              {" "}
              RARELY NEEDED. When checked, bypasses the &quot;already complete/analyzed&quot; guard and re-downloads the
              video even if one already exists.
            </TBody>
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>
              <TBody {...formStyles.label}>Max video length (minutes)</TBody>
            </div>
            <InputNumber<number>
              value={maxVideoLengthInMinutes}
              onChange={v => setMaxVideoLengthInMinutes(v ?? undefined)}
              min={1}
              style={{ width: 120 }}
              placeholder="default (10)"
            />
            <TBody color="#999">
              {" "}
              Overrides the default 10-minute cap on both download and analysis. Longer videos cost more and take longer
              to process.
            </TBody>
          </div>
        </div>
      </>
    );
  }
);

const TagsReprocessForm = React.memo(
  ({
    recipe,
    onMessagesChanged,
  }: {
    recipe: UserRecipe | AppUserRecipe | ExternalRecipe;
    onMessagesChanged: OnMessagesChanged;
  }) => {
    useEffect(() => {
      const payload: ProcessTags = { type: "processTags", recipeId: recipe.id };
      onMessagesChanged([{ messageType: "recipes/ingestionMessage", message: payload }]);
    }, []);

    return (
      <TBody>
        Re-runs tag processing for this recipe. Use this to apply updated tag rules without reprocessing the full
        recipe.
      </TBody>
    );
  }
);

const formStyles = {
  label: { scale: 1.2 as const },
  labelContainer: { margin: "10px 0" },
};
